import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { privacy } from "../i18n/privacy";
import { BRAND } from "../config/brand";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { Reveal } from "../components/Reveal";

const fill = (text, values) => text.replace(/\{(controller|email)\}/g, (_, k) => values[k]);

export default function PrivacyPolicy() {
  const { lang } = useLang();
  const p = privacy[lang];
  const values = { ...p.placeholders, ...Object.fromEntries(Object.entries(BRAND.legal).filter(([, v]) => v)) };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `SYNrise — ${p.label}`;
    return () => { document.title = "SYNrise — Rinasci. Evolvi. Investi."; };
  }, [p.label]);

  return (
    <div className="min-h-[100svh]" data-testid="privacy-page">
      <header className="sticky top-0 z-50 border-b border-white/5 bg-ink/80 backdrop-blur-xl" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-3 px-5 sm:px-8">
          <Link to="/" data-testid="privacy-back-link" className="flex items-center gap-2 text-sm text-zinc-300 transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" />
            <img src={BRAND.logoSmall} alt="" className="h-7 w-7" />
            <span className="hidden sm:inline">{p.back}</span>
          </Link>
          <LanguageSwitch />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-14 sm:px-8 sm:pt-20">
        <Reveal>
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
            <ShieldCheck className="h-5 w-5 text-gold" />
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold tracking-tight text-zinc-50 sm:text-5xl" data-testid="privacy-title">{p.title}</h1>
          <p className="mt-3 font-mono text-[12px] text-zinc-400" data-testid="privacy-updated">{p.updated}</p>
          <p className="mt-8 text-base leading-relaxed text-zinc-300">{p.intro}</p>
        </Reveal>

        <div className="mt-12 space-y-10">
          {p.sections.map((s, i) => (
            <Reveal key={s.h}>
              <section data-testid={`privacy-section-${i + 1}`}>
                <h2 className="font-display text-lg font-semibold text-zinc-50 sm:text-xl">{s.h}</h2>
                {s.p?.map((para) => <p key={para.slice(0, 30)} className="mt-3 text-[15px] leading-relaxed text-zinc-300">{fill(para, values)}</p>)}
                {s.list && (
                  <ul className="mt-4 space-y-3">
                    {s.list.map((item) => (
                      <li key={item.slice(0, 30)} className="flex gap-3 text-[15px] leading-relaxed text-zinc-300">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        <Link to="/" data-testid="privacy-back-bottom" className="mt-16 inline-flex h-12 items-center gap-2 rounded-full border border-white/10 px-6 text-sm font-semibold text-zinc-200 transition-colors hover:border-gold/40 hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> {p.back}
        </Link>
      </main>
    </div>
  );
}
